import { useEffect, useState, useMemo } from 'react';
import { listVendorCommissions, approveVendorCommission, cancelVendorCommission } from '@/features/vendor/api/commissions';
import { getPackageDisplayName } from '@/lib/packageDisplay';
import '@/features/super-admin/commissions/commissions.css';
import { ShoppingBag, ArrowRight, PackageSearch, Download, Filter } from 'lucide-react';

// تبدیل اعداد به فارسی
const toPersianDigits = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.toString().replace(/\d/g, (d) => persianDigits[d]).replace(/\./g, '٫');
};

const fmt = (n) => {
    const num = Number(n) || 0;
    return toPersianDigits(new Intl.NumberFormat('en-US').format(num));
};

const fmtPercent = (n) => {
    const val = Number(n);
    const formatted = val % 1 === 0 ? val.toString() : val.toFixed(2);
    return toPersianDigits(formatted) + '٪';
};

// تابع تشخیص دلیل تعلق پورسانت
const getLevelBadge = (level) => {
    if (level === 1) return { text: 'معرف مستقیم خریدار', bg: '#dbeafe', color: '#1e40af' };
    if (level === 2) return { text: 'معرف سطح ۲', bg: '#fef3c7', color: '#92400e' };
    if (level === 3) return { text: 'معرف سطح ۳', bg: '#fce7f3', color: '#9d174d' };
    return { text: `معرف سطح ${toPersianDigits(level)}`, bg: '#f1f5f9', color: '#475569' };
};

export default function VendorCommissionsPage() {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedPackageId, setSelectedPackageId] = useState(null);

    // استیت‌های مربوط به فیلترها
    const [minPayout, setMinPayout] = useState(0);
    // 👈 [ADDED] استیت برای فیلتر وضعیت (پیش‌فرض همه)
    const [statusFilter, setStatusFilter] = useState('all');

    // استیت برای نمایش لودینگ روی هر ردیف هنگام تغییر وضعیت
    const [updatingRowId, setUpdatingRowId] = useState(null);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await listVendorCommissions();
            setSales(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const packagesList = useMemo(() => {
        const map = new Map();
        sales.forEach(sale => {
            if (!map.has(sale.package_id)) {
                map.set(sale.package_id, {
                    id: sale.package_id,
                    name: getPackageDisplayName(sale.package_name),
                    salesCount: 0,
                    totalCommissions: 0,
                    allCommissions: []
                });
            }
            const pkg = map.get(sale.package_id);
            pkg.salesCount += 1;

            sale.commissions.forEach(c => {
                pkg.allCommissions.push({
                    ...c,
                    buyer_name: sale.buyer_name,
                    order_id: sale.order_id
                });
                pkg.totalCommissions += Number(c.amount);
            });
        });
        return Array.from(map.values());
    }, [sales]);

    const selectedPackage = useMemo(() => {
        return packagesList.find(p => p.id === selectedPackageId);
    }, [packagesList, selectedPackageId]);

    // 👈 [CHANGED] فیلتر و مرتب‌سازی لیست بر اساس حداقل مبلغ و وضعیت
    const filteredCommissions = useMemo(() => {
        if (!selectedPackage) return [];

        let list = [...selectedPackage.allCommissions];

        // فیلتر حداقل مبلغ
        if (minPayout > 0) {
            list = list.filter(c => Number(c.amount) >= Number(minPayout));
        }

        // فیلتر وضعیت
        if (statusFilter !== 'all') {
            list = list.filter(c => c.status === statusFilter);
        }

        // مرتب‌سازی بر اساس مبلغ
        list.sort((a, b) => Number(b.amount) - Number(a.amount));

        return list;
    }, [selectedPackage, minPayout, statusFilter]);

    // تغییر وضعیت فقط شامل حالت پرداخت شده است
    const handleStatusChange = async (commissionId, newStatus, currentStatus) => {
        if (newStatus === currentStatus) return;

        if (newStatus === 'pending') {
            alert('امکان بازگشت به وضعیت "در انتظار" وجود ندارد.');
            return;
        }

        setUpdatingRowId(commissionId);
        try {
            if (newStatus === 'paid') {
                await approveVendorCommission(commissionId);
            }
            await load(); // رفرش لیست
        } catch (e) {
            alert('خطا در تغییر وضعیت: ' + e.message);
            await load(); // برگرداندن مقدار قبلی در صورت خطا
        } finally {
            setUpdatingRowId(null);
        }
    };

    // تابع خروجی اکسل (CSV) - دقیقا از لیست فیلتر شده استفاده میکند
    const exportToExcel = () => {
        if (filteredCommissions.length === 0) {
            alert('لیستی برای خروجی وجود ندارد.');
            return;
        }

        const headers = ['ردیف', 'دریافت‌کننده (معرف)', 'شماره شبا', 'خریدار', 'درصد پورسانت', 'مبلغ پورسانت (ریال)', 'وضعیت'];

        const rows = filteredCommissions.map((c, index) => [
            index + 1,
            c.beneficiary_name || '',
            c.sheba || c.beneficiary_sheba || 'ثبت نشده',
            c.buyer_name || '',
            c.percent,
            c.amount,
            c.status === 'paid' ? 'پرداخت شده' : c.status === 'pending' ? 'در انتظار' : 'لغو شده'
        ]);

        let csvContent = "\uFEFF";
        csvContent += headers.join(",") + "\n";

        rows.forEach(rowArray => {
            const row = rowArray.map(val => `"${val}"`).join(",");
            csvContent += row + "\n";
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `لیست_پورسانت_${selectedPackage.name}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading && sales.length === 0) {
        return <div style={{ textAlign: 'center', padding: '3rem' }}>در حال دریافت اطلاعات...</div>;
    }

    return (
        <div className="commissions-page" style={{ direction: 'rtl', paddingTop: 0, width: '100%' }}>

            {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}

            {/* نمای اول: لیست سبدها */}
            {!selectedPackage && (
                <>
                    {packagesList.length === 0 ? (
                        <div className="empty-state">هنوز هیچ سبدی از شما خریداری نشده است.</div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1.5rem' }}>
                            {packagesList.map(pkg => (
                                <div
                                    key={pkg.id}
                                    style={{
                                        background: '#fff',
                                        borderRadius: '12px',
                                        border: '1px solid #e5e7eb',
                                        padding: '1.5rem',
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '320px',
                                        maxWidth: '100%'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
                                        <div style={{ background: '#eff6ff', padding: '0.6rem', borderRadius: '50%' }}>
                                            <ShoppingBag size={24} color="#2563eb" />
                                        </div>
                                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>{pkg.name}</h3>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#64748b', marginBottom: '0.5rem' }}>
                                        <span>تعداد فروش:</span>
                                        <strong style={{ color: '#1e293b' }}>{fmt(pkg.salesCount)} بار</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#64748b', marginBottom: '0.5rem' }}>
                                        <span>مبلغ کل پورسانت‌ها:</span>
                                        <strong style={{ color: '#059669' }}>{fmt(pkg.totalCommissions)} ریال</strong>
                                    </div>

                                    <button
                                        onClick={() => setSelectedPackageId(pkg.id)}
                                        style={{
                                            marginTop: '1.5rem',
                                            width: '100%',
                                            background: '#2563eb',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '0.7rem',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        مشاهده جزئیات
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* نمای دوم: جدول جزئیات سبد انتخاب شده */}
            {selectedPackage && (
                <>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        gap: '1rem',
                        background: '#fff',
                        padding: '0.75rem 1.25rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        flexWrap: 'wrap'
                    }}>
                        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            لیست پورسانت‌های فروش
                            <span style={{ fontSize: '0.85rem', color: '#2563eb', background: '#eff6ff', padding: '0.2rem 0.6rem', borderRadius: '4px', fontWeight: 'normal' }}>
                                {selectedPackage.name}
                            </span>
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginRight: 'auto', flexWrap: 'wrap' }}>

                            {/* فیلتر حداقل مبلغ */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f8fafc', padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                <Filter size={16} color="#64748b" />
                                <label style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 'bold' }}>حداقل مبلغ (ریال):</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="10000"
                                    value={minPayout}
                                    onChange={(e) => setMinPayout(e.target.value)}
                                    style={{
                                        width: '120px',
                                        padding: '0.3rem 0.5rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        outline: 'none',
                                        fontSize: '0.85rem',
                                        direction: 'ltr',
                                        textAlign: 'left'
                                    }}
                                    placeholder="مثلا 100000"
                                />
                            </div>

                            {/* 👈 [ADDED] فیلتر وضعیت */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f8fafc', padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                <label style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 'bold' }}>وضعیت:</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{
                                        padding: '0.3rem 0.5rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        outline: 'none',
                                        fontSize: '0.85rem',
                                        cursor: 'pointer',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <option value="all">نمایش همه</option>
                                    <option value="pending">در انتظار</option>
                                    <option value="paid">پرداخت شده</option>
                                </select>
                            </div>

                            <button
                                onClick={exportToExcel}
                                style={{
                                    background: '#10b981',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <Download size={16} /> خروجی اکسل
                            </button>

                            <button
                                onClick={() => {
                                    setSelectedPackageId(null);
                                    setMinPayout(0);
                                    setStatusFilter('all'); // 👈 ریست کردن فیلتر وضعیت هنگام بازگشت
                                }}
                                style={{
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <ArrowRight size={16} /> بازگشت به لیست
                            </button>
                        </div>
                    </div>

                    <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <tr>
                                    <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>دریافت‌کننده پورسانت (معرف)</th>
                                    <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>شماره شبا</th>
                                    <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>خریدار</th>
                                    <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>درصد</th>
                                    <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>مبلغ پورسانت</th>
                                    {/* 👈 [CHANGED] حذف کلمه (تغییر) از هدر */}
                                    <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>وضعیت</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredCommissions.map((c, idx) => {
                                    const badge = getLevelBadge(c.level);
                                    const shebaNumber = c.sheba || c.beneficiary_sheba || 'ثبت نشده';
                                    const isRowUpdating = updatingRowId === c.id;

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s', opacity: isRowUpdating ? 0.6 : 1 }}>
                                            <td style={{ padding: '0.6rem 0.8rem', color: '#1e293b', textAlign: 'center' }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{c.beneficiary_name}</div>
                                                <div style={{
                                                    fontSize: '0.7rem',
                                                    marginTop: '3px',
                                                    background: badge.bg,
                                                    color: badge.color,
                                                    display: 'inline-block',
                                                    padding: '1px 6px',
                                                    borderRadius: '4px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {badge.text}
                                                </div>
                                            </td>
                                            <td style={{ padding: '0.6rem 0.8rem', color: '#475569', fontSize: '0.85rem', textAlign: 'center', direction: 'ltr' }}>
                                                {shebaNumber !== 'ثبت نشده' ? <span style={{fontFamily: 'monospace'}}>{shebaNumber}</span> : <span style={{color: '#ef4444'}}>ثبت نشده</span>}
                                            </td>
                                            <td style={{ padding: '0.6rem 0.8rem', color: '#1e293b', fontWeight: 'bold', fontSize: '0.85rem', textAlign: 'center' }}>{c.buyer_name}</td>
                                            <td style={{ padding: '0.6rem 0.8rem', textAlign: 'center', color: '#10b981', fontWeight: 'bold', fontSize: '0.85rem' }}>{fmtPercent(c.percent)}</td>
                                            <td style={{ padding: '0.6rem 0.8rem', color: '#2563eb', fontWeight: 'bold', fontSize: '0.85rem', textAlign: 'center' }}>{fmt(c.amount)} ریال</td>

                                            <td style={{ padding: '0.6rem 0.8rem', textAlign: 'center' }}>
                                                {isRowUpdating ? (
                                                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>در حال ذخیره...</span>
                                                ) : (
                                                    <select
                                                        value={c.status}
                                                        onChange={(e) => handleStatusChange(c.id, e.target.value, c.status)}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e1',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            backgroundColor: c.status === 'paid' ? '#dcfce7' : c.status === 'pending' ? '#fef3c7' : '#fee2e2',
                                                            color: c.status === 'paid' ? '#15803d' : c.status === 'pending' ? '#92400e' : '#b91c1c',
                                                            outline: 'none'
                                                        }}
                                                    >
                                                        <option value="pending" disabled>در انتظار</option>
                                                        <option value="paid">پرداخت شده</option>
                                                    </select>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredCommissions.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                            <PackageSearch size={28} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
                                            {minPayout > 0 || statusFilter !== 'all'
                                                ? 'هیچ پورسانتی با فیلترهای انتخاب شده یافت نشد.'
                                                : 'هیچ پورسانتی برای این سبد ثبت نشده است.'}
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}